import { useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Search, Download, FileText, User, Calendar, ChevronDown, ChevronRight, Unlock, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const AdminDocuments = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [financialYear, setFinancialYear] = useState("all");
  const [requestIdFilter, setRequestIdFilter] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState({});

  const isSuperAdmin = user?.admin_role === "super_admin";
  const canUnlock = isSuperAdmin || (user?.permissions || []).includes("unlock_documents");


  const getFinancialYearOptions = () => {
    const now = new Date();
    const currentYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const options = [];

    for (let i = 0; i < 5; i++) {
      const start = currentYear - i;
      const end = start + 1;
      options.push(`${start}-${end}`);
    }

    return options;
  };

  const fetchData = async (targetPage = page) => {
    const trimmedSearch = searchTerm.trim();
    const trimmedRequestId = requestIdFilter.trim();

    const hasAnyFilter =
      trimmedSearch !== "" ||
      trimmedRequestId !== "" ||
      statusFilter !== "all" ||
      financialYear !== "all";

    if (!hasAnyFilter) {
      setHasSearched(true);
      setDocuments([]);
      setRequests([]);
      setExpandedRequests({});
      setTotalDocuments(0);
      toast.error("Please apply at least one filter before searching");
      return;
    }

    setLoading(true);

    try {
      const params = {
        page: targetPage,
        limit,
      };

      if (trimmedSearch) params.q = trimmedSearch;
      if (trimmedRequestId) params.request_id = trimmedRequestId;
      if (statusFilter !== "all") params.status = statusFilter;
      if (financialYear !== "all") params.financial_year = financialYear;

      const docsRes = await api.get("/admin/documents", { params });
      const payload = docsRes.data || {};
      const docs = payload.items || [];

      setDocuments(docs);
      setTotalDocuments(payload.total || 0);
      setHasSearched(true);
      setPage(payload.page || targetPage);

      const requestMap = new Map();

      docs.forEach((doc) => {
        if (!requestMap.has(doc.request_id)) {
          requestMap.set(doc.request_id, {
            id: doc.request_id,
            user_name: doc.user_name || "-",
            user_email: doc.user_email || "-",
            plan_name: doc.plan_name || "-",
            financial_year: doc.financial_year || "-",
          });
        }
      });

      const derivedRequests = Array.from(requestMap.values());
      setRequests(derivedRequests);

      const expanded = {};
      derivedRequests.slice(0, 3).forEach((r) => {
        expanded[r.id] = true;
      });
      setExpandedRequests(expanded);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchData(1);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setFinancialYear("all");
    setRequestIdFilter("");
    setDocuments([]);
    setRequests([]);
    setExpandedRequests({});
    setHasSearched(false);
    setPage(1);
    setTotalDocuments(0);
  };

  const handleReview = (doc) => {
    setSelectedDoc(doc);
    setNewStatus(doc.status);
    setAdminNotes(doc.admin_notes || "");
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    
    setUpdating(true);
    try {
      await api.put(`/admin/documents/${selectedDoc.id}/status`, {
        status: newStatus,
        admin_notes: adminNotes
      });
      toast.success("Document status updated");
      setSelectedDoc(null);
      fetchData(page);
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleUnlockDocument = async (docId) => {
    if (!window.confirm("This will allow the client to replace this approved document. Continue?")) return;
    
    try {
      await api.post(`/admin/documents/${docId}/allow-change`);
      toast.success("Document unlocked for replacement");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlock document");
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const res = await api.get(`/documents/${docId}/download`);

      if (res.data.download_url) {
        const link = document.createElement("a");
        link.href = res.data.download_url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = res.data.file_name || fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (res.data.file_data) {
        const link = document.createElement("a");
        link.href = `data:application/octet-stream;base64,${res.data.file_data}`;
        link.download = res.data.file_name || fileName;
        link.click();
      } else {
        throw new Error("No downloadable file returned");
      }

      if (res.data.password) {
        toast.info(`Document Password: ${res.data.password}`, { duration: 10000 });
      } else {
        toast.success("Download started");
      }
    } catch (err) {
      toast.error("Failed to download document");
    }
  };

  const toggleRequest = (requestId) => {
    setExpandedRequests(prev => ({
      ...prev,
      [requestId]: !prev[requestId]
    }));
  };

  // Group documents by request
  const getDocumentsByRequest = (requestId) => {
    return documents.filter((doc) => doc.request_id === requestId);
  };

  // Filter requests based on search and status
  const filteredRequests = requests.filter((req) => {
    const reqDocs = getDocumentsByRequest(req.id);
    const term = searchTerm.toLowerCase().trim();

    if (!reqDocs.length) return false;

    const matchesSearch =
      !term ||
      (req.user_name || "").toLowerCase().includes(term) ||
      (req.user_email || "").toLowerCase().includes(term) ||
      (req.plan_name || "").toLowerCase().includes(term) ||
      (req.id || "").toLowerCase().includes(term) ||
      reqDocs.some((doc) =>
        (doc.document_type || "").toLowerCase().includes(term) ||
        (doc.file_name || "").toLowerCase().includes(term) ||
        (doc.name || "").toLowerCase().includes(term)
      );

    if (statusFilter === "all") return matchesSearch;

    return matchesSearch && reqDocs.some((doc) => doc.status === statusFilter);
  });

  const statusCounts = {
    all: totalDocuments,
    pending: statusFilter === "pending" ? totalDocuments : null,
    approved: statusFilter === "approved" ? totalDocuments : null,
    rejected: statusFilter === "rejected" ? totalDocuments : null,
    needs_revision: statusFilter === "needs_revision" ? totalDocuments : null,
  };
  
  const totalVisibleRequests = filteredRequests.length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>Document Review</h1>
          <p className="opacity-70 mt-1">Review and approve all uploaded documents organized by case</p>
          <p className="text-sm opacity-50 mt-1">Documents are loaded only after applying filters.</p>
        </div>

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              type="button"
              onClick={(e) => {
                setStatusFilter(status);
                e.currentTarget.blur();
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === status
                  ? ''
                  : 'bg-white border hover:bg-gray-50'
              }`}
              style={
                statusFilter === status
                  ? {
                      backgroundColor: 'var(--primary)',
                      color: '#ffffff',
                      WebkitTextFillColor: '#ffffff'
                    }
                  : {
                      borderColor: 'var(--border)',
                      color: 'var(--primary)',
                      WebkitTextFillColor: 'var(--primary)'
                    }
              }
              data-testid={`filter-${status}`}
            >
                {status === 'all'
                ? `All (${count})`
                : `${status.replace('_', ' ')}${count !== null ? ` (${count})` : ''}`}
            </button>
          ))}
        </div>

        {/* Search */}
         <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" size={20} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by client name, email, file name, document type, document name"
              className="pl-10"
              data-testid="doc-search-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </div>

          <Input
            value={requestIdFilter}
            onChange={(e) => setRequestIdFilter(e.target.value)}
            placeholder="Request ID"
            data-testid="request-id-filter"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />

          <Select value={financialYear} onValueChange={setFinancialYear}>
            <SelectTrigger data-testid="financial-year-filter">
              <SelectValue placeholder="Financial Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Financial Years</SelectItem>
              {getFinancialYearOptions().map((fy) => (
                <SelectItem key={fy} value={fy}>
                  {fy}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handleSearch}
            className="btn-primary"
            data-testid="doc-search-btn"
          >
            Search
          </Button>

          <Button
            variant="outline"
            onClick={handleResetFilters}
            data-testid="doc-reset-btn"
          >
            Reset
          </Button>
        </div>

        {/* Documents Organized by Request */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : !hasSearched ? (
          <div className="empty-state bg-white rounded-xl border" style={{ borderColor: 'var(--border)' }}>
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Apply filters and click Search to load documents</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="empty-state bg-white rounded-xl border" style={{ borderColor: 'var(--border)' }}>
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No documents found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => {
              const reqDocs = getDocumentsByRequest(request.id);
              const pendingCount = reqDocs.filter(d => d.status === 'pending').length;
              const isExpanded = expandedRequests[request.id];
              
              return (
                <div 
                  key={request.id} 
                  className="bg-white rounded-xl border overflow-hidden"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {/* Request Header - Clickable */}
                  <button
                    onClick={() => toggleRequest(request.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    data-testid={`request-toggle-${request.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
                        <User size={18} />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold">{request.user_name}</p>
                        <p className="text-sm opacity-60">{request.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium">{request.plan_name}</p>
                        <p className="text-sm opacity-60 flex items-center gap-1 justify-end">
                          <Calendar size={14} />
                          FY {request.financial_year}
                        </p>
                        <p className="text-xs opacity-50 mono mt-1 select-text">
                          Req ID: {request.id}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{reqDocs.length} docs</Badge>
                        {pendingCount > 0 && (
                          <Badge className="bg-orange-100 text-orange-700">{pendingCount} pending</Badge>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Documents List */}
                  {isExpanded && (
                    <div className="border-t px-4 pb-4" style={{ borderColor: 'var(--border)' }}>
                      <div className="grid gap-3 mt-4">
                        {reqDocs.length === 0 ? (
                          <p className="text-center py-4 opacity-60">No documents uploaded yet</p>
                        ) : (
                          reqDocs.map((doc) => (
                            <div 
                              key={doc.id} 
                              className="flex items-center justify-between p-4 rounded-lg"
                              style={{ backgroundColor: 'var(--secondary)' }}
                              data-testid={`doc-item-${doc.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <FileText size={24} style={{ color: 'var(--accent)' }} />
                                <div>
                                    <p className="font-medium">{doc.name}</p>
                                    <p className="text-sm opacity-60">
                                      {doc.document_type} • {new Date(doc.uploaded_at).toLocaleDateString()}
                                    </p>
                                    <p className="text-xs opacity-50">
                                      File: {doc.file_name}
                                    </p>
                                    {doc.password && (
                                    <p className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                                      <Lock size={12} /> Password protected
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={`status-${doc.status}`}>{doc.status.replace('_', ' ')}</Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(doc.id, doc.file_name)}
                                  data-testid={`download-${doc.id}`}
                                >
                                  <Download size={16} />
                                </Button>
                                {doc.status === 'approved' && canUnlock && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleUnlockDocument(doc.id)}
                                    className="text-orange-600 border-orange-300"
                                    data-testid={`unlock-${doc.id}`}
                                  >
                                    <Unlock size={16} />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    handleReview(doc)
                                    e.currentTarget.blur()
                                  }}
                                  className="btn-primary"
                                  data-testid={`review-${doc.id}`}
                                >
                                  Review
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {hasSearched && totalDocuments > 0 && (
          <div
            className="flex items-center justify-between bg-white rounded-xl border p-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm opacity-70">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalDocuments)} of {totalDocuments} documents • {totalVisibleRequests} cases on this page
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => fetchData(page - 1)}
                data-testid="docs-prev-page"
              >
                Previous
              </Button>

              <Button
                variant="outline"
                disabled={page * limit >= totalDocuments || loading}
                onClick={() => fetchData(page + 1)}
                data-testid="docs-next-page"
              >
                Next
              </Button>
            </div>
          </div>
          )}
      </div>

      {/* Review Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>Review Document</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
                <div className="flex items-center gap-3">
                  <FileText size={24} style={{ color: 'var(--accent)' }} />
                  <div>
                      <p className="font-medium">{selectedDoc.name}</p>
                      <p className="text-sm opacity-60">{selectedDoc.document_type}</p>
                      <p className="text-sm opacity-60">Uploaded by: {selectedDoc.user_name}</p>
                      <p className="text-xs opacity-50">File: {selectedDoc.file_name}</p>
                      <p className="text-xs opacity-50">Plan: {selectedDoc.plan_name || "-"}</p>
                      <p className="text-xs opacity-50">FY: {selectedDoc.financial_year || "-"}</p>
                      <p className="text-xs opacity-50 mono">Request ID: {selectedDoc.request_id}</p>
                      {selectedDoc.password && (
                      <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <Lock size={12} /> Password: {selectedDoc.password}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleDownload(selectedDoc.id, selectedDoc.file_name)}
              >
                <Download size={16} className="mr-2" /> Download to Review
              </Button>
              
              <div>
                <label className="text-sm font-medium">Update Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-2" data-testid="doc-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="needs_revision">Needs Revision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Notes for Client</label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes (e.g., reason for rejection, what needs to be fixed)"
                  className="mt-2"
                  rows={4}
                  data-testid="doc-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedDoc(null)}>Cancel</Button>
            <Button onClick={(e) => {
                      handleUpdateStatus();
                      e.currentTarget.blur();
                    }} 
                    disabled={updating} 
                    className="btn-primary"
                    data-testid="update-doc-status-btn">
              {updating ? <span className="spinner" style={{ width: 20, height: 20 }}></span> : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminDocuments;
